import { Cog, Filter, Spline, Wrench, SlidersHorizontal } from "lucide-react";

const improvements = [
  {
    id: "01",
    title: "Impeller Back-face Vanes",
    short: "Vane-driven hydraulic boost.",
    description:
      "We removed the parasitic collector entirely. Custom back-face vanes accelerate the coolant tangentially before it hits the secondary-pump inlet, building meaningful pre-pressure without ever choking the main impeller.",
    icon: Cog,
    image: "/assets/ewp1110_render6.png",
    imagePosition: "object-right",
    badge: "PROPRIETARY DESIGN",
    span: "lg:col-span-2 lg:row-span-2",
  },
  {
    id: "02",
    title: "Spring Retainer Inlet Passage",
    short: "Laminar feed geometry.",
    description:
      "Smooth inlet, no obstacles, no turbulence. Higher-velocity coolant enters the inner cavity cleanly.",
    icon: Spline,
    image: "/assets/ewp1110_render5.png",
    imagePosition: "object-center",
    badge: "FLOW-OPTIMIZED",
  },
  {
    id: "03",
    title: "Integrated Inlet Filter",
    short: "Debris stops at the gate.",
    description:
      "A purpose-engineered filter mesh in the inlet passage blocks abrasive particles before they reach the rubber impeller or by-pass passages. No clogs. No vane wear.",
    icon: Filter,
    image: "/assets/ewp1110_render2.png",
    imagePosition: "object-center",
    badge: "FAIL-SAFE ENABLER",
  },
  {
    id: "04",
    title: "Reinforced Return Spring",
    short: "Thicker wire, higher pre-load.",
    description:
      "We rewound the main piston return spring with a heavier wire gauge and a tuned rate so it always overcomes secondary pressure on shutdown — guaranteeing the open-position fail-safe.",
    icon: SlidersHorizontal,
    image: "/assets/ewp1110_render3.png",
    imagePosition: "object-left",
    badge: "OPEN-STATE BIAS",
    span: "lg:col-span-2",
  },
];

export default function Engineering() {
  return (
    <section
      id="engineering"
      data-testid="engineering-section"
      className="relative py-28 lg:py-36 bg-[#F4F6FA] border-y border-[#C8D3DF] overflow-hidden"
    >
      <div className="absolute inset-0 tech-grid opacity-20 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16 reveal">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-5">
              <Wrench size={16} className="text-[#5B9BD5]" />
              <span className="spec-label">Four Improvements · One Failure Mode Eliminated</span>
            </div>
            <h2 className="font-chivo font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-[#1A2232] leading-[0.95]">
              The MPS MWP1110 solution,<br />redesigned in <span className="text-[#5B9BD5]">four key improvements</span>.
            </h2>
          </div>
          <p className="font-plex text-base text-[#536073] max-w-md leading-relaxed">
            Each improvement targets a documented OEM or aftermarket failure mode. Together they form a switching pump that performs better — and fails better — than anything else on the market.
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-5 lg:gap-6 auto-rows-[260px]">
          {improvements.map((it, idx) => {
            const Icon = it.icon;
            return (
              <div
                key={it.id}
                data-testid={`improvement-card-${it.id}`}
                className={`reveal group relative bg-white border border-[#C8D3DF] rounded-sm overflow-hidden hover:border-[#5B9BD5]/50 transition-all duration-500 ${
                  it.span || "lg:col-span-2"
                }`}
                style={{ transitionDelay: `${idx * 60}ms` }}
              >
                {/* hover glow */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#5B9BD5]/0 via-[#5B9BD5]/0 to-[#5B9BD5]/0 group-hover:from-[#5B9BD5]/8 group-hover:to-transparent transition-all duration-700" />

                {/* image */}
                <div className="absolute inset-0 opacity-55 group-hover:opacity-85 transition-opacity duration-700">
                  <img
                    src={it.image}
                    alt={`${it.title} engineering diagram`}
                    className={`w-full h-full object-contain ${it.imagePosition || "object-center"}`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-white via-white/70 to-transparent" />
                  <div className="absolute inset-0 bg-gradient-to-r from-white/80 via-transparent to-transparent" />
                </div>

                {/* content */}
                <div className="relative h-full flex flex-col justify-between p-6 lg:p-7">
                  <div className="flex items-start justify-between">
                    <div className="w-10 h-10 border border-[#C8D3DF] group-hover:border-[#5B9BD5]/60 flex items-center justify-center rounded-sm transition-colors">
                      <Icon size={16} className="text-[#5B9BD5]" />
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className="font-mono text-[10px] tracking-[0.25em] text-[#8896A6] uppercase">
                        {it.id} / 04
                      </span>
                      <span className="px-2 py-0.5 border border-[#5B9BD5]/40 bg-[#5B9BD5]/5 font-mono text-[9px] tracking-[0.2em] text-[#5B9BD5] uppercase">
                        {it.badge}
                      </span>
                    </div>
                  </div>

                  <div>
                    <div className="font-mono text-[10px] tracking-[0.25em] text-[#5B9BD5] uppercase mb-2">
                      {it.short}
                    </div>
                    <h3 className="font-chivo font-bold text-xl lg:text-2xl tracking-tight text-[#1A2232] mb-3">
                      {it.title}
                    </h3>
                    <p className="font-plex text-sm text-[#536073] leading-relaxed max-w-md">
                      {it.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
